/**
 * The sample for facility inspections: for every facility and trade, two
 * detailed inspections a few months apart and two recent daily rounds, and
 * three years of repairs. Every record carries `sample: true`, and the screen
 * shows it as sample and offers to remove it.
 *
 * Generated rather than written out, from a fixed seed, so it is the same on
 * every load and in every test. Facilities that are worse in the sample are
 * worse for a stated reason — plant and utilities run harder than offices, and
 * a critical facility is inspected more closely — not at random.
 */
import {
  TRADES,
  formItems,
  type Fault,
  type InspectionRecord,
  type InspectionStore,
  type RepairRecord,
  type Result,
  type Trade,
  type TradeId,
} from "../domain/assetInspections";
import { FACILITY_LIST, type FacilityRef } from "../domain/facilityRegister";

/** mulberry32: small, fast, and the same sequence for the same seed */
function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const hash = (s: string) => [...s].reduce((h, c) => (Math.imul(h, 31) + c.charCodeAt(0)) >>> 0, 7);

/** Which trades look after which facility. Civil works areas have no cleaning or pest round. */
export function tradesFor(f: FacilityRef): TradeId[] {
  if (f.area) return ["civil", "electrical", "utilities"];
  if (/substation/i.test(f.name)) return ["civil", "electrical", "fire", "pest"];
  return TRADES.map((t) => t.id);
}

/** How hard a facility is worked, 0–1: plant and utilities hardest, housing and offices least. */
function wear(f: FacilityRef): number {
  if (/Utilities|electrical|Electrical distribution/i.test(f.group)) return 0.7;
  if (f.group === "Terminal" || f.group === "Pilgrim facilities") return 0.6;
  if (f.group === "Civil works") return 0.55;
  if (f.group === "Housing" || f.group === "Administration") return 0.3;
  return 0.45;
}

const COST: Record<TradeId, [number, number]> = {
  civil: [2500, 38000],
  electrical: [1200, 26000],
  electromech: [1800, 45000],
  fire: [900, 18000],
  utilities: [1500, 32000],
  cleaning: [400, 6000],
  pest: [300, 4500],
};

const NOTES: Record<Exclude<Result, "ok" | "na">, string[]> = {
  minor: ["Noted for the next visit", "Cosmetic; no effect on operation", "Monitor"],
  major: ["Needs repair this month", "Degrading since the last inspection", "Affects part of the facility"],
  critical: ["Unsafe — make safe today", "Out of service", "Regulatory non-compliance"],
};

function faultsFor(trade: Trade, form: "daily" | "detailed", badness: number, r: () => number): Fault[] {
  const out: Fault[] = [];
  for (const it of formItems(trade, form)) {
    const x = r();
    let result: Exclude<Result, "ok"> | null = null;
    if (x < 0.03) result = "na";
    /* a critical finding is rare, and should read as rare: a handful across the airport, not one per building */
    /* severity concentrates where the wear is worst: most pairs see minor findings, a few see the rest */
    else if (x < 0.03 + Math.max(0, badness - 0.9) * 0.04) result = "critical";
    else if (x < 0.03 + Math.max(0, badness - 0.9) * 0.34 + badness * 0.022) result = "major";
    else if (x < 0.03 + Math.max(0, badness - 0.9) * 0.4 + badness * 0.16) result = "minor";
    if (!result) continue;
    const pick = <T,>(xs: T[]) => xs[Math.floor(r() * xs.length)];
    out.push(
      result === "na"
        ? { itemId: it.id, result }
        : { itemId: it.id, result, note: pick(NOTES[result]), repairType: pick(trade.repairTypes) },
    );
  }
  return out;
}

const iso = (t: number) => new Date(t).toISOString();
const DAY = 86_400_000;

export function sampleInspectionStore(now: number = Date.now()): InspectionStore {
  const inspections: InspectionRecord[] = [];
  const history: RepairRecord[] = [];
  for (const f of FACILITY_LIST) {
    for (const tid of tradesFor(f)) {
      const trade = TRADES.find((t) => t.id === tid)!;
      const r = rng(hash(`${f.id}|${tid}`));
      /* most pairs are in fair order; a few are not — the tail is what a forecast is for */
      const u = r();
      const base = wear(f) * (0.2 + u * u * u * 3.2);
      /* the earlier detailed inspection, then a later one a little worse or better */
      const drift = (r() - 0.35) * 0.5;
      const plan: [("daily" | "detailed"), number, number][] = [
        ["detailed", 200 + Math.floor(r() * 90), base],
        ["detailed", 20 + Math.floor(r() * 50), Math.max(0.05, base + drift)],
        ["daily", 3 + Math.floor(r() * 4), Math.max(0.05, base + drift)],
        ["daily", Math.floor(r() * 2), Math.max(0.05, base + drift)],
      ];
      plan.forEach(([form, daysAgo, badness], i) => {
        const company = trade.defaultContractor;
        inspections.push({
          id: `SMP-${f.id}-${tid}-${i + 1}`,
          facilityId: f.id,
          trade: tid,
          form,
          at: iso(now - daysAgo * DAY - Math.floor(r() * 8) * 3_600_000),
          inspector: form === "daily" ? `${trade.label} supervisor` : `${trade.label} inspector`,
          company,
          /* a daily round is a walk-through: it sees less than a detailed inspection does */
          faults: faultsFor(trade, form, form === "daily" ? badness * 0.45 : badness, r),
          sample: true,
        });
      });
      /* three years of repairs, more where the facility is worked harder */
      const n = Math.round((1 + wear(f) * 5 + (4 - f.crit)) * (0.35 + r() * 0.6));
      const [lo, hi] = COST[tid];
      for (let k = 0; k < n; k++) {
        const opened = now - Math.floor(r() * 3 * 365) * DAY;
        const closed = opened + Math.floor(1 + r() * 30) * DAY;
        history.push({
          id: `SMP-R-${f.id}-${tid}-${k + 1}`,
          facilityId: f.id,
          trade: tid,
          repairType: trade.repairTypes[Math.floor(r() * trade.repairTypes.length)],
          openedAt: iso(opened),
          closedAt: closed < now ? iso(closed) : undefined,
          cost: Math.round((lo + r() * r() * (hi - lo)) / 50) * 50,
          sample: true,
        });
      }
    }
  }
  return { version: 1, contractors: {}, inspections, history };
}
